import { useResizeObserver } from '@/hooks';
import { useCallback, useMemo, useRef, useState } from 'react';
import { selectBundlePageScale, selectBundlePagesData, useAppSelector } from '@/store';
import { calculatePageSizes } from '../utils';

export const useReactVirtualizedListResize = () => {
  const bundlePageScale = useAppSelector(selectBundlePageScale);
  const bundlePagesData = useAppSelector(selectBundlePagesData);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const { maxPageWidth, maxPageWidthHeight } = useMemo(() => {
    let maxPageWidth = 0;
    let maxPageWidthHeight = 0;

    if (!bundlePagesData) return { maxPageWidth, maxPageWidthHeight };

    if (bundlePagesData.size) {
      bundlePagesData.forEach((data) => {
        if (data.pageDimensions.width > maxPageWidth) {
          maxPageWidth = data.pageDimensions.width;
          maxPageWidthHeight = data.pageDimensions.height;
        }
      });
    }
    return { maxPageWidth, maxPageWidthHeight };
  }, [bundlePagesData]);

  // Start the container wide enough to hold pages at a typical high-zoom
  // level, without needing to grow later. Every change to container width
  // forces react-virtualized to rebuild its internal grid, and that rebuild
  // knocks row-offset caches out of sync with the DOM — producing a
  // visible "drift" where the viewport briefly lands on the wrong page.
  //
  // Baseline zoom of 3 covers typical usage (100-300%). Wider-than-viewport
  // space sits invisibly to the sides — the scrollLeft centring effect in
  // ReactVirtualizedList keeps pages visually centred over the viewport.
  const BASELINE_ZOOM = 3;

  const onResize = useCallback<ResizeObserverCallback>(
    (entries) => {
      const [entry] = entries;

      if (entry) {
        const { pageWidth: recalculatedMaxPageWidth } = calculatePageSizes({
          height: maxPageWidthHeight,
          width: maxPageWidth,
          bundlePageScale: bundlePageScale,
          containerHeight: entry.contentRect.height,
        });

        // Baseline width: widest page rendered at BASELINE_ZOOM. Container
        // starts here so users get no rebuild-drift up to that zoom level.
        const { pageWidth: baselinePageWidth } = calculatePageSizes({
          height: maxPageWidthHeight,
          width: maxPageWidth,
          bundlePageScale: BASELINE_ZOOM,
          containerHeight: entry.contentRect.height,
        });

        // Container width only grows, never shrinks. Once it's grown to
        // accommodate a given zoom, it stays there for the session.
        setContainerHeight(entry.contentRect.height);
        setContainerWidth((prev) => {
          const required = Math.max(recalculatedMaxPageWidth, entry.contentRect.width, baselinePageWidth);
          return Math.max(prev, required);
        });
      }
    },
    [bundlePageScale, maxPageWidth, maxPageWidthHeight],
  );

  useResizeObserver(containerRef.current, {}, onResize);

  const setSizesManually = (newHeight: number, newWidth: number) => {
    setContainerHeight(newHeight);
    setContainerWidth(newWidth);
  };

  return {
    containerRef,
    containerHeight,
    containerWidth,
    setSizesManually,
  };
};
